import { CommonModule } from '@angular/common';
import { Component, effect, input, output, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTree, MatTreeModule } from '@angular/material/tree';
import { TranslocoModule } from '@jsverse/transloco';
import type { OrgaItemBaseModel } from '../../../../models';

type OrgaItemWithElementType = OrgaItemBaseModel & {
  elementType?: { type?: string };
};

/**
 * BusinessUnitOrganizationalStructureTabComponent
 *
 * Displays the organizational structure tree for a business unit
 */
@Component({
  selector: 'dtis-admin-business-unit-organizational-structure-tab',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatTreeModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslocoModule,
  ],
  host: { class: 'block max-h-full h-full overflow-auto' },
  template: `
    <div class="p-6">
      <div class="flex items-center justify-between">
        <h2 class="text-title-large mb-6">
          {{ 'organizational-structure' | transloco }}
        </h2>
        <div class="buttons flex gap-2">
          <button
            mat-icon-button
            (click)="expandAll()"
          >
            <mat-icon>unfold_more</mat-icon>
          </button>
          <button
            mat-icon-button
            (click)="collapseAll()"
          >
            <mat-icon>unfold_less</mat-icon>
          </button>
        </div>
      </div>
      
      @if (rootItem(); as root) {
        <mat-card appearance="outlined" class="h-full">
          <mat-card-content >
            <mat-tree [dataSource]="treeData()" [childrenAccessor]="childrenAccessor">
              <!-- Tree node template for expandable nodes -->
              <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChildren" [isExpandable]="true" class="">
                <div class="flex items-center cursor-pointer bg-surface hover:bg-onSurfaceVariant/10 rounded-full w-full h-min-touch" matTreeNodeToggle>
                  <button mat-icon-button
                          (click)="toggleNode(node)"
                          [attr.aria-label]="'toggle ' + node.name"
                          [class.rotate-90]="isNodeExpanded(node)">
                    <mat-icon class="mat-icon-rtl-mirror">chevron_right</mat-icon>
                  </button>
                  <span class="text-body-large">{{ node.name }}</span>
                  @if (getChildrenCount(node) > 0 && getChildrenType(node)) {
                    <mat-chip class="h-6! text-xs! ml-2!">
                      {{ getChildrenCount(node) }} {{ getOrgTypePlural(getChildrenType(node)!) | transloco }}
                    </mat-chip>
                  }
                  @if (isAdmin()) {
                    <div class="ml-auto flex gap-1">
                      @if (canAddChild(node)) {
                        <button
                          mat-icon-button
                          (click)="onCreateChild(node); $event.stopPropagation()"
                          [matTooltip]="'add-' + getChildTypeSingular(node) | transloco"
                        >
                          <mat-icon>add</mat-icon>
                        </button>
                      }
                      @if (canDelete(node)) {
                        <button
                          mat-icon-button
                          (click)="onDeleteItem(node); $event.stopPropagation()"
                          [matTooltip]="'delete' | transloco"
                        >
                          <mat-icon class="text-error!">delete</mat-icon>
                        </button>
                      }
                    </div>
                  }
                </div>
                <ul [class.d-none]="!isNodeExpanded(node)">
                  <ng-container matTreeNodeOutlet></ng-container>
                </ul>
              </mat-nested-tree-node>
              
              <!-- Tree node template for leaf nodes -->
              <mat-nested-tree-node *matTreeNodeDef="let node" >
                <div class="flex items-center cursor-pointer bg-surface hover:bg-onSurfaceVariant/10 rounded-full w-full h-min-touch" matTreeNodeToggle>
                  @if (canAddChild(node)) {
                    <button mat-icon-button disabled>
                      <mat-icon class="invisible">chevron_right</mat-icon>
                    </button>
                  }
                  <span class="text-body-large font-medium">{{ node.name }}</span>
                  @if (isAdmin()) {
                    <div class="ml-auto flex gap-1">
                      @if (canAddChild(node)) {
                        <button
                          mat-icon-button
                          (click)="onCreateChild(node); $event.stopPropagation()"
                          [matTooltip]="'add-' + getChildTypeSingular(node) | transloco"
                        >
                          <mat-icon>add</mat-icon>
                        </button>
                      }
                      @if (canDelete(node)) {
                        <button
                          mat-icon-button
                          (click)="onDeleteItem(node); $event.stopPropagation()"
                          [matTooltip]="'delete' | transloco"
                        >
                          <mat-icon class="text-error!">delete</mat-icon>
                        </button>
                      }
                    </div>
                  }
                </div>
              </mat-nested-tree-node>
            </mat-tree>
          </mat-card-content>
        </mat-card>
      } @else if (isLoading()) {
        <div class="flex justify-center py-8">
          <mat-progress-spinner mode="indeterminate" diameter="40" />
        </div>
      }
    </div>
  `,
  styles: [
    `
    mat-tree {
      ::ng-deep {
        .mat-tree-node {
          min-height: 48px;
          padding-left: 24px;
          display: flex;
          align-items: center;
          gap: 8px;
          position: relative;
        }

        mat-nested-tree-node {
          ul {
            padding-left: 24px;
            list-style-type: none;
            margin: 0;
          }

        }

        .d-none {
          display: none;
        }
      }
    }
  `,
  ],
})
export class BusinessUnitOrganizationalStructureTabComponent {
  rootItem = input.required<OrgaItemBaseModel | null>();
  isLoading = input<boolean>(false);
  isAdmin = input<boolean>(false);

  createChild = output<OrgaItemBaseModel>();
  deleteItem = output<OrgaItemBaseModel>();

  /**
   * Data source for tree (root node)
   */
  treeData = signal<OrgaItemBaseModel[]>([]);

  /**
   * Children accessor for tree structure
   */
  childrenAccessor = (node: OrgaItemBaseModel) => node.children ?? [];

  /**
   * Set of expanded node identifiers
   */
  expandedNodes = signal<Set<string>>(new Set());

  /**
   * Reference to the MatTree component
   */
  tree?: MatTree<OrgaItemBaseModel>;

  @ViewChild(MatTree) set matTree(tree: MatTree<OrgaItemBaseModel>) {
    this.tree = tree;
  }

  constructor() {
    // Update tree data when root item changes
    effect(() => {
      const root = this.rootItem();
      if (root) {
        this.treeData.set([root]);
      } else {
        this.treeData.set([]);
      }
    });
  }

  /**
   * Predicate to check if node has children
   */
  hasChildren = (_: number, node: OrgaItemBaseModel): boolean => {
    return !!node.children && node.children.length > 0;
  };

  /**
   * Get count of children for a node
   */
  getChildrenCount(node: OrgaItemBaseModel): number {
    return node.children?.length ?? 0;
  }

  /**
   * Get type of children nodes
   */
  getChildrenType(node: OrgaItemBaseModel): string | null {
    if (!node.children || node.children.length === 0) {
      return null;
    }
    return node.children[0].type;
  }

  /**
   * Get plural translation key for organization type
   */
  getOrgTypePlural(type: string): string {
    const pluralMap: Record<string, string> = {
      UseCaseRoot: 'roots',
      Master: 'masters',
      Instance: 'instances',
      Configuration: 'configurations',
    };
    return pluralMap[type] ?? `${type.toLowerCase()}s`;
  }

  /**
   * Resolve the type of a node, supporting both flat `type` and nested `elementType.type`
   */
  private resolveType(node: OrgaItemBaseModel): string {
    const nodeWithElementType = node as OrgaItemWithElementType;
    return node.type ?? nodeWithElementType.elementType?.type ?? '';
  }

  /**
   * Get singular translation key for child type based on parent node type
   */
  getChildTypeSingular(node: OrgaItemBaseModel): string {
    const childTypeMap: Record<string, string> = {
      UseCaseRoot: 'master',
      Master: 'instance',
      Instance: 'configuration',
    };
    return childTypeMap[this.resolveType(node)] ?? 'item';
  }

  /**
   * Check if a child can be added to this node
   * Configuration nodes (last level) cannot have children
   */
  canAddChild(node: OrgaItemBaseModel): boolean {
    // Configuration is the last level in the hierarchy
    return this.resolveType(node) !== 'Configuration';
  }

  /**
   * Check if a node can be deleted
   * Root nodes and nodes with children cannot be deleted
   */
  canDelete(node: OrgaItemBaseModel): boolean {
    // Cannot delete root node
    const nodeType = this.resolveType(node);
    if (nodeType === 'ROOT' || nodeType === 'UseCaseRoot') {
      return false;
    }
    // Cannot delete nodes with children
    if (node.children && node.children.length > 0) {
      return false;
    }
    return true;
  }

  /**
   * Handle create child action
   */
  onCreateChild(node: OrgaItemBaseModel): void {
    this.createChild.emit(node);
  }

  /**
   * Handle delete item action
   */
  onDeleteItem(node: OrgaItemBaseModel): void {
    this.deleteItem.emit(node);
  }

  /**
   * Check if a node is expanded
   */
  isNodeExpanded(node: OrgaItemBaseModel): boolean {
    if (!node.identifier) {
      return false;
    }
    return this.expandedNodes().has(node.identifier);
  }

  /**
   * Toggle node expansion state
   */
  toggleNode(node: OrgaItemBaseModel): void {
    if (!node.identifier) {
      return;
    }
    const expanded = new Set(this.expandedNodes());
    if (expanded.has(node.identifier)) {
      expanded.delete(node.identifier);
    } else {
      expanded.add(node.identifier);
    }
    this.expandedNodes.set(expanded);
  }

  /**
   * Expand all tree nodes
   */
  expandAll(): void {
    const allNodes = new Set<string>();
    const collectNodes = (node: OrgaItemBaseModel) => {
      if (this.hasChildren(0, node) && node.identifier) {
        allNodes.add(node.identifier);
        node.children?.forEach((child) => {
          collectNodes(child);
        });
      }
    };
    this.treeData().forEach((node) => {
      collectNodes(node);
    });
    this.expandedNodes.set(allNodes);
  }

  /**
   * Collapse all tree nodes
   */
  collapseAll(): void {
    this.expandedNodes.set(new Set());
  }
}
